/**
 * `tanstackToBootClient` — adapter that bridges a real TanStack v5
 * `QueryClient` to the abstract `BootQueryClient` shape that
 * `runSDKBootSequence` operates against.
 *
 * The abstract `BootQueryClient` (in `SDKProvider-boot-order.tsx`) is
 * deliberately structural — it lets the boot-sequence tests pin the
 * phase ordering without depending on the real TanStack runtime. This
 * adapter is what the SDKProvider hands to `runSDKBootSequence` at
 * mount time.
 *
 * KEY ASYMMETRY · per-mutation `.subscribe(cb)`
 * ---------------------------------------------
 * The abstract shape assumes a `mutation.subscribe(cb)` method per
 * Mutation instance. Real TanStack only exposes `subscribe` on the
 * `MutationCache` — not on `Mutation`. The shim filters
 * `mutationCache.subscribe(...)` by reference equality on `event.mutation`
 * so the abstract per-mutation contract is preserved.
 */

import type { MutationCache, QueryClient } from '@tanstack/react-query';

// Mutation-level type imported with broader generics to bridge variance
// between TanStack's `Mutation<TData, Error, ...>` and our abstract shape.
type AnyMutation = ReturnType<MutationCache['getAll']>[number];

import type { BootQueryClient, MutationFn } from './SDKProvider-boot-order.js';

interface AbstractMutationLike {
  readonly state: { readonly isPaused: boolean };
  readonly options: { readonly scope?: { readonly id?: string } };
  subscribe(cb: (ev: { readonly type: string }) => void): () => void;
}

/**
 * Wraps `qc` to expose the `BootQueryClient` shape. The returned object
 * delegates every call to `qc`; only `getMutationCache().getAll()` walks
 * mutations and synthesises the per-mutation `subscribe` shim.
 */
export function tanstackToBootClient(qc: QueryClient): BootQueryClient {
  const mc: MutationCache = qc.getMutationCache();

  function wrapMutation(m: AnyMutation): AbstractMutationLike {
    const scopeId = m.options.scope?.id;
    return {
      state: { isPaused: m.state.isPaused },
      options: scopeId !== undefined ? { scope: { id: scopeId } } : {},
      subscribe(cb): () => void {
        // Filter cache-level subscribe by reference equality on
        // `event.mutation` — TanStack does not expose per-mutation
        // subscribe directly, but `MutationCache.subscribe` carries the
        // mutation ref on every event so this is the canonical pattern.
        const unsubscribe = mc.subscribe((event) => {
          if (event.mutation !== m) return;
          // The abstract type only cares about `event.type`; map directly.
          cb({ type: event.type });
        });
        return unsubscribe;
      },
    };
  }

  return {
    setMutationDefaults(key, options): void {
      qc.setMutationDefaults(key, {
        mutationFn: options.mutationFn as MutationFn,
        ...(options.scope !== undefined ? { scope: options.scope } : {}),
      });
    },
    getMutationCache(): ReturnType<BootQueryClient['getMutationCache']> {
      return {
        getAll(): ReadonlyArray<AbstractMutationLike> {
          return mc.getAll().map(wrapMutation);
        },
      };
    },
    resumePausedMutations(): Promise<unknown> {
      return qc.resumePausedMutations();
    },
  };
}
